/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, TextField, Tooltip, Avatar, Grid, Divider } from '@mic3/platform-ui';
import produce from 'immer';

import { isEmpty, getStr } from 'app/utils/utils';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import { bind, memoize, debounce } from 'app/utils/decorators/decoratorUtils';
import ListItem from 'app/components/molecules/List/ListItem';
import Alert from 'app/components/molecules/Alert/Alert';
import Immutable from 'app/utils/immutable/Immutable';
import AddResourcesModal from 'app/containers/Common/AddResourcesModal';
import AddEntityByPrimaryClassModal from 'app/containers/Common/AddEntityByPrimaryClassModal';
import VirtualListManaged from 'app/components/molecules/VirtualList/VirtualListManaged';
import {
    addWorkspaceResources,
    removeWorkspaceResources,
    clearResourcesList,
    addWorkspaceOwners,
    removeWorkspaceOwners,
    loadWorkspacePermissions,
} from 'store/actions/admin/workspacesActions';

export const getResourcesListAsPerType = (list, resourceType) => {
    const entityList = (list || []).map(({ entity }) => entity).filter(Boolean);
    switch (resourceType) {
        case 'class':
        case 'user':
        case 'permission':
        case 'primaryClass':
        case 'task':
        case 'process':
        case 'script':
        case 'processdefinition':
        case 'formdefinition':
            return list;
        case 'person':
            return (entityList || []).filter(({ type }) => ['user', 'person'].includes(type));
        default:
            return entityList.filter(({ type }) => type === resourceType);
    }
};

const ListItemStyled = styled(ListItem)`
    width: 95%;
    max-width: 1024px;
    margin: 0 auto;
    ${({ selected }) => (selected ? 'background: rgba(30, 168, 207, 0.2)' : '')};
    ${({ disabled }) => (!disabled ? 'cursor: pointer;' : '')}
    margin-top: 10px;
`;
const SearchField = styled(TextField)`
    .MuiFilledInput-input {
        padding: 10px 12px 10px 0 !important;
        background: ${({ theme }) => theme.base.background};
    }
`;

const StyledAvatar = styled(Avatar)`
    && {
        ${({ selected }) => (selected ? 'background-color: gray !important' : '')};
    }
`;
const StyledGrid = styled(Grid)`
    margin: 0 8px;
    position: sticky !important;
    top: 0;
    background: ${({ theme }) => theme.material.colors.background.default} !important;
    z-index: 999;
`;

const StyledDivider = styled(Divider)`
    margin-bottom: 15px !important;
`;

const StyledContentArea = styled(ContentArea)`
    ${({ $lazyLoad }) => $lazyLoad && `
        overflow: hidden !important;
        height: 100%;
        display: flex;
        flex-direction: column;
    `}
`;

const inputProps = { disableUnderline: true };

/**
 * Container that is used to display the Entities tab of the Groups & Permissions details view.
 */
class WorkspaceResourcesList extends PureComponent {
    static propTypes = {
        disabled: PropTypes.bool,
        lazyLoad: PropTypes.bool, // Enable lazy loading with VirtualListManaged
        totalRecords: PropTypes.number, // Total count for lazy loading
        loadData: PropTypes.func, // Function to load more data
        isLoading: PropTypes.bool, // Loading state
    };

    static defaultProps = {
        disabled: false,
        lazyLoad: false,
    };

    state = Immutable({
        searchValue: '',
        selectedListItems: [],
        isModalOpen: false,
        lazyListKey: Date.now(),
    });

    defaultState = { ...this.state};

    listRef = React.createRef();

    @bind
    setDefaultState() {
        this.setState(this.defaultState);
    }

    @bind
    onChangeSearch(event) {
        const searchValue = event.target.value;
        this.setState({ searchValue });

        // If lazy loading is enabled, debounce search to update filters
        if (this.props.lazyLoad) {
            this.debouncedSearchHandler(searchValue);
        }
    }

    @bind
    @debounce(1000)
    debouncedSearchHandler() {
        if (!this.props.id) return;
        this.setState({ lazyListKey: Date.now()});
    }

    @bind
    @memoize()
    getListAsPerType(list, resourceType) {
        return getResourcesListAsPerType(list, resourceType);
    }

    @bind
    searchForFrom(searchQuery, data) {
        if (!data || !searchQuery) return false;
        return ['name', 'id', 'username', 'primary.username'].some(field => {
            let value = getStr(data, field, '');
            if (!value) return false;
            value = value.toLowerCase();
            const query = searchQuery.toLowerCase();
            return value.includes(query);
        });
    }

    @bind
    @memoize()
    filterSearchedData(searchQuery, data) {
        return searchQuery ? (data || []).filter(item => this.searchForFrom(searchQuery, item)) : data || [];
    }

    @bind
    async removeWorkspaceResources() {
        const { id, type, removeWorkspaceOwners, loadWorkspacePermissions, lazyLoad } = this.props;
        const { selectedListItems } = this.state;
        if (!id || isEmpty(selectedListItems)) {
            return;
        }
        if (type === 'user') {
            const ownerIds = selectedListItems.map(item => item.id);
            await removeWorkspaceOwners(id, ownerIds);
        } else {
            await this.props.removeWorkspaceResources(id, selectedListItems, type);
            if( type === 'permission'){
                loadWorkspacePermissions(id);
            } else if (lazyLoad) {
                this.setState({ lazyListKey: Date.now() });
            }
        }
        this.setDefaultState();
    }

    @bind
    async onSubmitResources(submittedList) {
        const { id, type, addWorkspaceResources, addWorkspaceOwners, loadWorkspacePermissions, lazyLoad } = this.props;
        if (!id || !submittedList || isEmpty(submittedList)) {
            return;
        }
        if (type === 'user') {
            const ownerIds = submittedList.map(item => item.id);
            await addWorkspaceOwners(id, ownerIds);
        } else {
            await addWorkspaceResources(id, submittedList, type);
            if( type === 'permission'){
                loadWorkspacePermissions(id);
            } else if (lazyLoad) {
                this.setState({ lazyListKey: Date.now() });
            }
        }
        this.setDefaultState();
    }

    @bind
    buildList(list) {
        const { disabled } = this.props;
        return (list || []).map(data => {
            const { id, name } = data;
            const selected = !isEmpty(this.getSelectedListItem(id));
            return (
                <ListItemStyled
                    key={id}
                    component={<StyledAvatar selected={selected} initials={name} />}
                    title={name}
                    subTitle={`#${id}`}
                    onClick={e => !disabled && this.onSelectListItem(e, data)}
                    selected={selected}
                    raised
                    disabled={disabled}
                />
            );
        });
    }

    @bind
    onSelectListItem(event, data) {
        event.preventDefault();
        const selectedItem = this.getSelectedListItem(data.id);
        if (isEmpty(selectedItem)) {
            this.setState(
                produce(draft => {
                    draft.selectedListItems.push(data);
                })
            );
        } else {
            this.setState(prevState => ({ selectedListItems: prevState.selectedListItems.filter(({ id }) => id !== data.id) }));
        }
    }

    @bind
    getSelectedListItem(itemId) {
        if (!itemId) {
            return {};
        }
        const { selectedListItems } = this.state;
        return selectedListItems.find(({ id }) => id === itemId) || {};
    }

    @bind
    openModal() {
        this.setState({ isModalOpen: true });
    }

    @bind
    closeModal() {
        this.props.clearResourcesList();
        this.setDefaultState();
    }

    @bind
    @memoize()
    buildRenderComponent(selectedListItems) {
        return ({ style, index, data }) => {
            if (!data) {
                return <div style={style} key={index} />;
            }

            const { id, name } = data;
            const { disabled } = this.props;
            const selected = !isEmpty(selectedListItems.find(item => item.id === id));

            return (
                <div style={style} key={id}>
                    <ListItemStyled
                        component={<StyledAvatar selected={selected} initials={name} />}
                        title={name}
                        subTitle={`#${id}`}
                        onClick={e => !disabled && this.onSelectListItem(e, data)}
                        selected={selected}
                        raised
                        disabled={disabled}
                    />
                </div>
            );
        };
    }

    @bind
    buildLazyList(list, totalRecords) {
        const { isLoading, loadData, startIndex } = this.props;
        const { searchValue, selectedListItems, lazyListKey } = this.state;
        return (
            <VirtualListManaged
                key={lazyListKey}
                ref={this.listRef}
                itemSize={85}
                itemCount={totalRecords}
                list={list}
                startIndex={startIndex || 0}
                isLoading={!!isLoading}
                loadData={(options) => loadData({ ...options, searchValue})}
                renderComponent={this.buildRenderComponent(selectedListItems)}
                maxWidth="1024"
            />
        );
    }

    /**
     * @override
     */
    render() {
        const { title, list, type, disabled, lazyLoad, totalRecords, id, isLoading } = this.props;
        const resourceList = this.getListAsPerType(list, type);
        const filteredList = this.filterSearchedData(this.state.searchValue, resourceList);

        return (
            <StyledContentArea $lazyLoad={lazyLoad}>
                <StyledGrid>
                    <SearchField
                        onChange={this.onChangeSearch}
                        value={this.state.searchValue}
                        fullWidth
                        margin="none"
                        placeholder="Search..."
                        InputProps={inputProps}
                    />

                    <StyledDivider />

                    {!disabled && isEmpty(this.state.selectedListItems) ? (
                        <Tooltip arrow title={`Click to add ${(title || '').toLowerCase()} to this workspace`}>
                            <div>
                                <Button fullWidth onClick={this.openModal}>
                                    + Add new
                                </Button>
                            </div>
                        </Tooltip>
                    ) : (
                        !disabled && (
                            <Tooltip arrow title={`Click to remove ${(title || '').toLowerCase()} to this workspace`}>
                                <div>
                                    <Button fullWidth onClick={this.removeWorkspaceResources}>
                                        REMOVE
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    )}
                </StyledGrid>

                {lazyLoad ? (
                    this.buildLazyList(resourceList, totalRecords)
                ) : (
                    !isEmpty(filteredList) ? this.buildList(filteredList) : <Alert type="background">No workspace {(title || '').toLowerCase()} found</Alert>
                )}
                {type === 'primaryClass' ? (
                    <AddEntityByPrimaryClassModal
                        key="primaryClass"
                        isOpen={this.state.isModalOpen}
                        isBulkSelect
                        showActiveClasses={true}
                        addedResourcesList={resourceList}
                        onSubmit={this.onSubmitResources}
                        closeModal={this.closeModal}
                        resetOnMount
                        workspaceId={lazyLoad ? id : undefined}
                    />
                ) : (
                    <AddResourcesModal
                        key={type}
                        type={type}
                        isBulkSelect={true}
                        isOpen={this.state.isModalOpen}
                        addedResourcesList={resourceList}
                        onSubmit={this.onSubmitResources}
                        closeModal={this.closeModal}
                        workspaceId={lazyLoad ? id : undefined}
                        isLoading={isLoading}
                    />
                )}
            </StyledContentArea>
        );
    }
}

export default connect(null, {
    addWorkspaceResources,
    removeWorkspaceResources,
    clearResourcesList,
    addWorkspaceOwners,
    removeWorkspaceOwners,
    loadWorkspacePermissions,
})(WorkspaceResourcesList);
